/** Speakers of a transcript: who speaks how often, and same-speaker blocks joined for reading. */

import { wordsOrUndefined, concatWords, lastOf } from './blockEditing';

const SENTENCES_PER_GROUP = 8;

/** @returns {Array} [{ name, count }] sorted by block count, most first */
export const getSpeakersList = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) {
    return [];
  }

  const counts = new Map();
  blocks.forEach((block) => {
    const speaker = block.speaker || 'UNKNOWN';
    counts.set(speaker, (counts.get(speaker) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

const countSentences = (text) => (text ? (text.match(/[.?!]/g) || []).length : 0);

const mergeGroup = (group) => {
  if (group.length === 1) return group[0];
  const first = group[0];
  return {
    ...first,
    text: group.map((block) => block.text.trim()).filter(Boolean).join(' '),
    end: lastOf(group).end,
    words: wordsOrUndefined(concatWords(group)),
    metadata: {
      ...first.metadata,
      isGrouped: true,
      originalBlockIds: group.map((block) => block.id),
    },
  };
};

/**
 * Join consecutive blocks of the same speaker into groups of about
 * SENTENCES_PER_GROUP sentences.
 */
export const groupBlocksBySpeaker = (blocks) => {
  if (!blocks || blocks.length === 0) {
    return [];
  }

  const grouped = [];
  let group = [];
  let sentenceCount = 0;
  for (const block of blocks) {
    const blockSentences = countSentences(block.text);

    if (group.length > 0 && block.speaker !== group[0].speaker) {
      grouped.push(mergeGroup(group));
      group = [block];
      sentenceCount = blockSentences;
      continue;
    }

    group.push(block);
    sentenceCount += blockSentences;
    if (sentenceCount >= SENTENCES_PER_GROUP) {
      grouped.push(mergeGroup(group));
      group = [];
      sentenceCount = 0;
    }
  }
  if (group.length > 0) grouped.push(mergeGroup(group));
  return grouped;
};
